import VirtualBackgroundExtension from 'agora-extension-virtual-background';
import AgoraRTC, { ClientConfig, IAgoraRTCClient } from 'agora-rtc-react';
import React, { useContext, createContext, useState, useEffect } from 'react';

const config: ClientConfig = {
  mode: 'rtc',
  codec: 'h264',
};

const VideoCallContext = createContext<{
  showChat: boolean;
  setShowChat: (showChat: boolean) => void;
  enableVideo: boolean;
  setEnableVideo: (enableVideo: boolean) => void;
  enableMicrophone: boolean;
  setEnableMicrophone: (enableMicrophone: boolean) => void;
  resolution: VideoResolution;
  setResolution: (resolution: VideoResolution) => void;
  blurBackground: '0' | '1' | '2' | '3';
  setBlurBackground: (blurBackground: '0' | '1' | '2' | '3') => void;
  initialClient: IAgoraRTCClient;
  extension: any;
  enableVideoCall: boolean;
  setEnableVideoCall: (enableVideoCall: boolean) => void;
  showFullControls: boolean;
  setShowFullControls: (showFullControls: boolean) => void;
  cameras: MediaDeviceInfo[];
  setCameras: (cameras: MediaDeviceInfo[]) => void;
  currentCameraId: string | undefined;
  setCurrentCameraId: (currentCameraId: string | undefined) => void;
  unfilteredCameras: MediaDeviceInfo[];
  setUnfilteredCameras: (unfilteredCameras: MediaDeviceInfo[]) => void;
}>({
  showChat: false,
  setShowChat: () => {},
  enableVideo: true,
  setEnableVideo: () => {},
  enableMicrophone: true,
  setEnableMicrophone: () => {},
  resolution: '360p' as VideoResolution,
  setResolution: () => {},
  blurBackground: '0',
  setBlurBackground: () => {},
  initialClient: AgoraRTC.createClient(config),
  extension: new VirtualBackgroundExtension(),
  enableVideoCall: false,
  setEnableVideoCall: () => {},
  showFullControls: false,
  setShowFullControls: () => {},
  cameras: [],
  setCameras: () => {},
  currentCameraId: undefined,
  setCurrentCameraId: () => {},
  unfilteredCameras: [],
  setUnfilteredCameras: () => {},
});

export enum VideoResolution {
  '720p' = '720p',
  '360p' = '360p',
  '240p_4' = '240p_4',
}

const initialClient = AgoraRTC.createClient(config);
const extension = new VirtualBackgroundExtension();
AgoraRTC.registerExtensions([extension]);

const VideoCallProvider = ({ children }: { children: React.ReactNode }) => {
  const [enableVideoCall, setEnableVideoCall] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [enableVideo, setEnableVideo] = useState(true);
  const [enableMicrophone, setEnableMicrophone] = useState(true);
  const [showFullControls, setShowFullControls] = useState(false);
  const [resolution, setResolution] = useState<VideoResolution>(
    VideoResolution['360p'],
  );
  const [blurBackground, setBlurBackground] = useState<'0' | '1' | '2' | '3'>(
    '0',
  );
  const [cameras, setCameras] = useState<MediaDeviceInfo[]>([]);
  const [unfilteredCameras, setUnfilteredCameras] = useState<MediaDeviceInfo[]>(
    [],
  );
  const [currentCameraId, setCurrentCameraId] = useState<string | undefined>();

  return (
    <VideoCallContext.Provider
      value={{
        showChat,
        setShowChat,
        enableVideo,
        setEnableVideo,
        enableMicrophone,
        setEnableMicrophone,
        resolution,
        setResolution,
        blurBackground,
        setBlurBackground,
        initialClient,
        extension,
        enableVideoCall,
        setEnableVideoCall,
        showFullControls,
        setShowFullControls,
        cameras,
        setCameras,
        currentCameraId,
        setCurrentCameraId,
        unfilteredCameras,
        setUnfilteredCameras,
      }}
    >
      {children}
    </VideoCallContext.Provider>
  );
};

export default VideoCallProvider;

export const useVideoCall = () => {
  return useContext(VideoCallContext);
};
